/**
 * AttachmentSavePrompt — Mail PR B
 *
 * Long-specced UX (director Vanessa): when a user files an email that has
 * attachments via Bonsai Mail, ask BEFORE the upload happens:
 *
 *   "This email has N attachments. Save them?"   →   Yes / No / Configure
 *
 *   - Yes        → save attachments to the matter's default Documents
 *                  subfolder (the existing pre-PR-B behaviour, but now
 *                  recorded as an explicit user choice instead of a
 *                  fait accompli default).
 *   - No         → skip attachments entirely.
 *   - Configure  → expand a single-select picker showing the matter's
 *                  top-level folders, user picks the destination, save
 *                  the attachments there.
 *
 * Bulk-file UX: callers pass `mode="bulk"` and an `emailCount`.  The
 * decision applies to EVERY email in the bulk job — we do not prompt
 * per-email in bulk (that would be hostile UX).  Single-email file
 * actions get the per-email prompt.
 *
 * Contract with callers:
 *   - The prompt is ONLY opened when there are attachments.  Callers
 *     must short-circuit when `email.hasAttachments` is false and pass
 *     `include_attachments: false` straight through.  The prompt does
 *     NOT render anything when `attachmentCount === 0`.
 *   - Auto-file (confidence ≥ 0.95 countdown) MUST NOT open this prompt;
 *     auto is a deliberate "no friction" path — the audit row records
 *     `attachment_decision: "saved_default_auto"` so we can tell auto
 *     from user choice in the UAT register.
 *
 * The component does NOT fetch the folder list itself — the caller
 * passes `folders` (matter top-level only, from
 * GET /bonsai/mail/matters/{drive_id}/{item_id}/subfolders).  Single
 * source of truth lives with the caller, who already has the matter
 * context.
 */
import { useState, useCallback, useEffect } from "react";
import { Paperclip, FolderOpen, X, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { errorMessage } from "../utils/errorMessage";

// Surfaced error rendering — keep aligned with Vanessa's standing rule:
// never print [object Object], never render a stringified Error verbatim,
// never leak the bare axios "Request failed with status code N". The
// canonical formatter enforces all three.
function _formatError(err, fallback) {
  if (typeof err === "string") return err || fallback;
  return errorMessage(err, fallback);
}

export default function AttachmentSavePrompt({
  open,
  onOpenChange,
  attachmentCount = 0,
  emailCount = 1,
  mode = "single", // "single" | "bulk"
  defaultFolderId = null, // matter's Documents subfolder id, when known
  defaultFolderName = "Documents",
  folders = [], // [{ id, name, webUrl?, childCount? }, ...] — matter top-level
  loadingFolders = false,
  foldersError = null,
  onLoadFolders, // optional () => Promise<void> — caller-supplied lazy loader
  onDecision, // ({ include_attachments, attachment_folder_id, attachment_decision }) => void
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Reset internal state every time the prompt is reopened so a stale
  // selection from a previous email doesn't leak across.
  useEffect(() => {
    if (open) {
      setShowPicker(false);
      setSelectedFolderId(null);
      setSubmitting(false);
      setLocalError(null);
    }
  }, [open]);

  const _emit = useCallback(
    async (decision) => {
      if (submitting) return;
      setSubmitting(true);
      setLocalError(null);
      try {
        await onDecision(decision);
        onOpenChange?.(false);
      } catch (err) {
        // No false-success toasts — surface the failure to the user and
        // keep the modal open so they can retry.
        setLocalError(_formatError(err, "Failed to save attachments"));
        setSubmitting(false);
      }
    },
    [onDecision, onOpenChange, submitting]
  );

  const handleYes = useCallback(() => {
    _emit({
      include_attachments: true,
      attachment_folder_id: defaultFolderId || null,
      attachment_decision: "saved_default",
    });
  }, [_emit, defaultFolderId]);

  const handleNo = useCallback(() => {
    _emit({
      include_attachments: false,
      attachment_folder_id: null,
      attachment_decision: "skipped",
    });
  }, [_emit]);

  const handleConfigure = useCallback(async () => {
    setShowPicker(true);
    setLocalError(null);
    if (onLoadFolders) {
      try {
        await onLoadFolders();
      } catch (err) {
        setLocalError(_formatError(err, "Failed to load folders"));
      }
    }
  }, [onLoadFolders]);

  const handleSave = useCallback(() => {
    if (!selectedFolderId) return;
    const picked = folders.find((f) => f.id === selectedFolderId);
    const folderName = picked?.name || "selected folder";
    _emit({
      include_attachments: true,
      attachment_folder_id: selectedFolderId,
      attachment_decision: `saved_custom:${folderName}`,
    });
  }, [_emit, selectedFolderId, folders]);

  // The prompt should never render when there are no attachments — the
  // caller is responsible for short-circuiting, but belt-and-braces here.
  if (!open || !attachmentCount || attachmentCount <= 0) return null;

  const headline =
    mode === "bulk"
      ? `${emailCount} email${emailCount === 1 ? "" : "s"} have a total of ${attachmentCount} attachment${
          attachmentCount === 1 ? "" : "s"
        }. Save them?`
      : `This email has ${attachmentCount} attachment${
          attachmentCount === 1 ? "" : "s"
        }. Save them?`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="attachment-save-prompt"
        className="sm:max-w-[480px]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            Save attachments?
          </DialogTitle>
          <DialogDescription>{headline}</DialogDescription>
        </DialogHeader>

        {!showPicker && (
          <div className="flex flex-col gap-2 py-2">
            <Button
              data-testid="attachment-yes"
              onClick={handleYes}
              disabled={submitting}
              className="justify-start gap-2"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FolderOpen className="w-4 h-4" />
              )}
              Yes — save to {defaultFolderName}
            </Button>
            <Button
              data-testid="attachment-no"
              onClick={handleNo}
              disabled={submitting}
              variant="outline"
              className="justify-start gap-2"
            >
              <X className="w-4 h-4" />
              No — skip attachments
            </Button>
            <Button
              data-testid="attachment-configure"
              onClick={handleConfigure}
              disabled={submitting}
              variant="ghost"
              className="justify-start gap-2"
            >
              <FolderOpen className="w-4 h-4" />
              Configure — pick a folder
            </Button>
          </div>
        )}

        {showPicker && (
          <div className="flex flex-col gap-2 py-2 max-h-[320px] overflow-auto">
            <div className="text-sm text-slate-500 mb-1">
              Pick a destination folder for the attachments:
            </div>
            {loadingFolders && (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading folders…
              </div>
            )}
            {!loadingFolders && foldersError && (
              <div
                data-testid="attachment-folders-error"
                className="text-sm text-red-600 py-2"
              >
                {_formatError(foldersError, "Failed to load folders")}
              </div>
            )}
            {!loadingFolders && !foldersError && folders.length === 0 && (
              <div className="text-sm text-slate-500 py-2">
                No top-level folders available for this matter.
              </div>
            )}
            {!loadingFolders &&
              !foldersError &&
              folders.length > 0 &&
              folders.map((f) => (
                <label
                  key={f.id}
                  data-testid="attachment-folder-option"
                  className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer ${
                    selectedFolderId === f.id
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="attachment-folder"
                    value={f.id}
                    checked={selectedFolderId === f.id}
                    onChange={() => setSelectedFolderId(f.id)}
                  />
                  <FolderOpen className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">{f.name}</span>
                </label>
              ))}
          </div>
        )}

        {localError && (
          <div
            data-testid="attachment-prompt-error"
            className="text-sm text-red-600"
          >
            {localError}
          </div>
        )}

        <DialogFooter>
          {showPicker && (
            <>
              <Button
                variant="ghost"
                onClick={() => setShowPicker(false)}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                data-testid="attachment-save"
                onClick={handleSave}
                disabled={!selectedFolderId || submitting}
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Save here"
                )}
              </Button>
            </>
          )}
          {!showPicker && (
            <Button
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
